import { useEffect, useState } from "react";
import { Modal } from "../modal/Modal";
import { useTranslation } from "react-i18next";
import { ArrowUpDown, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";

interface TableReorderModalProps<
  T extends Record<string, unknown> = Record<string, unknown>
> {
  data: T[];
  onReorder: (list: T[]) => void;
}

export function TableReorderModal<
  T extends Record<string, unknown> = Record<string, unknown>
>({ data, onReorder }: TableReorderModalProps<T>) {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);
  const [items, setItems] = useState<T[]>([]);

  useEffect(() => {
    // refresh items when modal is open
    if (isOpen) {
      setItems([...data]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSave = () => {
    onReorder(items);
    setOpen(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="p-2 border border-gray-300 rounded-3xl hover:bg-blue-600 hover:text-white text-gray-900 dark:text-gray-100 dark:border-gray-600 dark:hover:bg-blue-600"
      >
        <ArrowUpDown className="w-4 h-4" />
      </button>

      <Modal
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        title={t("form.reorder")}
        closeIcon={true}
      >
        <div className="w-full max-w-lg md:min-w-[600px]">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((item) => item.id as string | number)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {items.map((item) => (
                  <SortableItem<T>
                    key={item.id as string | number}
                    id={item.id as string | number}
                    item={item}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <div className="mt-6 flex justify-end gap-2 sticky pt-2 pb-3 bottom-0 bg-white dark:bg-gray-800">
            <button
              onClick={() => setOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md
                     dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
            >
              {t("form.cancel")}
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700
                     dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              {t("form.validate")}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

interface SortableItemProps<
  T extends Record<string, unknown> = Record<string, unknown>
> {
  id: string | number;
  item: T;
}

function SortableItem<
  T extends Record<string, unknown> = Record<string, unknown>
>({ id, item }: SortableItemProps<T>) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between gap-4 py-2 px-3 select-none bg-white dark:bg-gray-700 rounded-lg border dark:border-gray-600"
    >
      <span className="text-gray-900 dark:text-gray-100">
        {(item.name || item.title || item.text || `Item ${id}`) as string}
      </span>
      <div {...attributes} {...listeners} className="cursor-grab">
        <GripVertical className="w-5 h-5 text-gray-500 dark:text-gray-400" />
      </div>
    </div>
  );
}
